package dev.youndie.site.pages

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.*
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.layout.NoLayout
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobwebx.frontmatter.*
import com.varabyte.kobwebx.markdown.*

@InitRoute
fun initPetichPage(ctx: InitRouteContext) {
    ctx.data.add(MarkdownContext("petich.md", FrontMatterElement.Builder { addScalar("title", "petich"); addScalar("searchTitle", "Distributed saga engine for Kotlin — compensation, suspend/resume, outbox"); addScalar("description", "A saga engine built around one question: what is left in the system if you die halfway. Steps as interceptors, compensation in reverse, an outbox in the same transaction."); addScalar("library", "petich"); addScalar("post", "/blog/what-is-left-if-you-die-halfway"); }))
}

@Page("/petich")
@Layout(".components.layouts.LibraryLayout")
@Composable
fun PetichPage() {
    org.jetbrains.compose.web.dom.H2(attrs = { id("why-it-exists") }) {
        org.jetbrains.compose.web.dom.Text("Why it exists")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("An operation spanning several services is not one database write. Reserve capacity, claim a quota, apply the change, hand it downstream, notify — any step can refuse, and by then some of the earlier ones are already irreversible. ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("try/catch")
        }
        org.jetbrains.compose.web.dom.Text(" does not help: what needs undoing is not a transaction but actions that already happened, in reverse, and only the ones that really did.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-you-get") }) {
        org.jetbrains.compose.web.dom.Text("What you get")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("One interceptor, both directions.")
            }
            org.jetbrains.compose.web.dom.Text(" A step declares ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("execute()")
            }
            org.jetbrains.compose.web.dom.Text(" and ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("compensate()")
            }
            org.jetbrains.compose.web.dom.Text(" in the same object, so undo cannot quietly drift from do.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Phases")
            }
            org.jetbrains.compose.web.dom.Text(" — ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("ENRICHMENT → VALIDATION → AUTHORIZATION → EXECUTION → POST_PROCESSING")
            }
            org.jetbrains.compose.web.dom.Text(" — with priority inside each.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Waiting for a human")
            }
            org.jetbrains.compose.web.dom.Text(" without holding a thread or a connection: the saga is a value, it goes to storage and resumes on a later request. With a ")
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("deadline")
            }
            org.jetbrains.compose.web.dom.Text(", because a suspended saga nobody returns to is a resource leak with better manners.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("An outbox in the same transaction as the state change")
            }
            org.jetbrains.compose.web.dom.Text(", which makes \"the work happened but the notification never went out\" structurally impossible. A repository without outbox support still works — the engine degrades visibly to no events rather than pretending.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Optimistic locking by version plus a per-saga mutex.")
        }
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("install") }) {
        org.jetbrains.compose.web.dom.Text("Install")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-kotlin") }) { org.jetbrains.compose.web.dom.Text("""repositories { maven("https://reposilite.kotlin.website/snapshots") }

dependencies {
    implementation("io.github.youndie:petich-core:0.1.0.2")
    implementation("io.github.youndie:petich-ktor:0.1.0.2")
    implementation("io.github.youndie:petich-postgres:0.1.0.2")
}
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("petich-outbox-core")
        }
        org.jetbrains.compose.web.dom.Text(", ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("petich-idempotency")
        }
        org.jetbrains.compose.web.dom.Text(" and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("petich-scheduler")
        }
        org.jetbrains.compose.web.dom.Text(" deliberately do ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("not")
        }
        org.jetbrains.compose.web.dom.Text(" depend on the core, and are usable on their own.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("petich-postgres")
        }
        org.jetbrains.compose.web.dom.Text(" ships no driver and no connection pool: it takes an Exposed ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("Database")
        }
        org.jetbrains.compose.web.dom.Text(" and does not care what is underneath. Choosing a driver is the application's decision.")
    }
}
